import React from 'react';
import { Lock, Trash2, ArrowLeft, Mail } from 'lucide-react';
import FormWizLogo from './FormWizLogo';

interface PrivacyPolicyProps {
    onBack: () => void;
}

const SUPPORT_EMAIL = 'info@epiphanyunltd.com';
const DELETION_URL = 'https://formwiz.app/delete-account';
const LAST_UPDATED = 'August 27, 2026';

interface BulletProps {
    title: string;
    children: React.ReactNode;
}

const Bullet: React.FC<BulletProps> = ({ title, children }) => (
    <li className="flex gap-3">
        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2.5 shrink-0"></div>
        <p><strong>{title}</strong> {children}</p>
    </li>
);

/**
 * Public privacy policy, served at /privacy.
 *
 * Must stay consistent with the in-app policy
 * (FormWizMobile/lib/features/legal/privacy_screen.dart) and with the Google
 * Play Data safety declaration for com.epiphanyunlimited.formwiz. If what we
 * collect or who we send it to changes, update all three together.
 */
const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-950 text-slate-900 dark:text-slate-100 font-sans p-6">
            <div className="max-w-3xl mx-auto">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 hover:text-blue-600 mb-8 transition-colors font-medium"
                >
                    <ArrowLeft size={20} /> Back
                </button>

                <div className="flex items-center gap-3 mb-4">
                    <FormWizLogo size={40} />
                    <h1 className="text-3xl font-bold">Privacy & Data Security</h1>
                </div>

                <p className="text-sm text-slate-500 dark:text-slate-400 mb-8 leading-relaxed">
                    FormWiz is operated by Epiphany Unlimited, Inc. This policy covers the
                    FormWiz website and the FormWiz Android app
                    (<code className="text-xs">com.epiphanyunlimited.formwiz</code>), and
                    explains what we collect, where it goes, and how to remove it.
                    <br />
                    Last updated: {LAST_UPDATED}
                </p>

                <div className="space-y-8">

                    <section className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm p-6 rounded-2xl border border-blue-100 dark:border-slate-700 shadow-sm">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-blue-100 dark:bg-blue-900/50 text-blue-600 rounded-xl">
                                <Lock size={24} />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold mb-2">Local-Only Storage</h2>
                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                    FormWiz operates on a <strong>Local-First</strong> principle. We do not
                                    maintain a central database of your filled forms, your answers, or your
                                    voice recordings. Your documents and the answers you give during an
                                    interview stay on your own device — in your browser's local storage on
                                    the web, and in a private, per-account area of app storage in the Android
                                    app, so files belonging to one account are never visible to another
                                    account on the same device.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold mb-4">What We Collect</h2>
                        <ul className="space-y-4 text-slate-600 dark:text-slate-300">
                            <Bullet title="Account information:">
                                Your name and email address, so you can sign in and so your plan follows you
                                across devices. Passwords are handled by our authentication provider and
                                stored only as a salted hash — we never see them.
                            </Bullet>
                            <Bullet title="Documents you choose:">
                                PDFs, and photos or images of forms (JPG, PNG) that you select. FormWiz opens
                                your file picker only when you choose to add a document and receives only the
                                file you pick. It has no access to the rest of your photo library and does
                                not scan it.
                            </Bullet>
                            <Bullet title="Your answers:">
                                The text you type or dictate to fill in a form. These stay on your device and
                                are never uploaded to us.
                            </Bullet>
                            <Bullet title="Subscription status:">
                                Whether your account is on the free or paid plan, and how many downloads you
                                have used, so plan limits can be enforced.
                            </Bullet>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold mb-4">How Your Data Is Used</h2>
                        <ul className="space-y-4 text-slate-600 dark:text-slate-300">
                            <Bullet title="Document analysis:">
                                To find the blank fields on a form, the document or photo you selected is
                                sent over an encrypted connection to Google Gemini, a third-party AI service,
                                which returns a description of the fields it found. The document is used only
                                to generate that response. Neither FormWiz nor the AI provider retains a copy
                                of it after analysis, and it is not used to train any model.
                            </Bullet>
                            <Bullet title="On-device analysis (Android):">
                                If you turn on On-Device AI in Settings, analysis runs entirely on your phone
                                using the Gemma model and on-device text recognition. In that mode your
                                documents and photos never leave the device at all.
                            </Bullet>
                            <Bullet title="Voice dictation:">
                                When you dictate an answer, your speech is transcribed by the speech
                                recognition service built into your browser or device, which may process
                                audio on its provider's servers under that provider's own privacy policy.
                                FormWiz receives only the resulting text. We do not record, store, or
                                transmit audio, and the microphone is active only while you have started
                                dictation.
                            </Bullet>
                            <Bullet title="Filling and saving:">
                                Your answers are written into the PDF on your device. Nothing about the
                                content of your form is transmitted to us when you save, preview, or download
                                it.
                            </Bullet>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold mb-4">What We Do Not Collect</h2>
                        <ul className="space-y-4 text-slate-600 dark:text-slate-300">
                            <Bullet title="No tracking:">
                                FormWiz contains no advertising SDKs, no analytics SDKs, and no third-party
                                trackers. We do not build an advertising profile of you, and we never sell or
                                rent personal information.
                            </Bullet>
                            <Bullet title="No location or contacts:">
                                We do not request or collect your location, contacts, calendar, call logs, or
                                messages.
                            </Bullet>
                            <Bullet title="No background access:">
                                Files are read only when you pick them, and the microphone is used only when
                                you start dictation.
                            </Bullet>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-bold mb-4">Who We Share With</h2>
                        <ul className="space-y-4 text-slate-600 dark:text-slate-300">
                            <Bullet title="Service providers only:">
                                We use Google (Gemini AI analysis), Netlify (hosting and account sign-in), and
                                Neon (the database holding your account record and plan). Each processes data
                                only to operate FormWiz on our behalf, under its own contractual privacy
                                commitments.
                            </Bullet>
                            <Bullet title="Payments:">
                                Purchases made on this website are processed by Stripe, which handles card
                                details directly. FormWiz never receives or stores your card number.
                            </Bullet>
                            <Bullet title="Legal:">
                                We may disclose information if required by law, or to protect the rights and
                                safety of our users — but we cannot disclose form contents or answers,
                                because we do not have them.
                            </Bullet>
                        </ul>
                    </section>

                    <section className="bg-red-50/50 dark:bg-red-900/10 backdrop-blur-sm p-6 rounded-2xl border border-red-100 dark:border-red-900/30">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-xl">
                                <Trash2 size={24} />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold mb-2">You Are in Control</h2>
                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                                    Because your forms and answers are stored on your device, you have total
                                    control over them. You can delete individual documents at any time.
                                    Clearing your browser's site data — or, in the Android app, clearing app
                                    data or uninstalling — permanently removes every saved form.
                                </p>
                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                                    To delete your account and everything stored with it — your name, email,
                                    sign-in credentials, plan record, and any payment customer record — open
                                    Account Settings and choose <strong>Delete Account</strong>. You can also
                                    request deletion at{' '}
                                    <a
                                        href={DELETION_URL}
                                        className="text-blue-600 dark:text-blue-400 font-semibold underline"
                                    >
                                        {DELETION_URL}
                                    </a>
                                    . Account deletion is immediate and permanent.
                                </p>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Deleting your account does not remove documents already saved on your own
                                    device; delete those in the app or clear your site data.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm p-6 rounded-2xl border border-indigo-100 dark:border-slate-700 shadow-sm">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 rounded-xl">
                                <Mail size={24} />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold mb-2">Questions & Support</h2>
                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                    FormWiz is not directed to children under 13, and we do not knowingly
                                    collect information from them. If this policy changes, we will update this
                                    page and the copy shown inside the app. For privacy questions, data
                                    requests, or support, contact Epiphany Unlimited, Inc. at{' '}
                                    <a
                                        href={`mailto:${SUPPORT_EMAIL}`}
                                        className="text-blue-600 dark:text-blue-400 font-semibold underline"
                                    >
                                        {SUPPORT_EMAIL}
                                    </a>
                                    .
                                </p>
                            </div>
                        </div>
                    </section>

                    <div className="text-center pt-8 border-t border-slate-200 dark:border-slate-800 text-sm text-slate-400">
                        <p>&copy; {new Date().getFullYear()} Epiphany Unlimited, Inc. All rights reserved.</p>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
